import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { GlobalConstants } from '../Constants/global.constants';

export interface SimulationTargetMapping {
  id: string;
  orgId: string;
  actualTarget: string;
  simulatedTarget: string;
}

export interface SimulationTargetMappingFilterRequest {
  pageNumber: number;
  pageSize: number;
  searchValue?: string | null;
  id?: string | null;
  actualTarget?: string | null;
  simulatedTarget?: string | null;
}

export interface SimulationTargetMappingFilterData {
  list: SimulationTargetMapping[];
  totalRecords: number;
}

export interface SimulationTargetMappingFilterResponse {
  isSuccess: boolean;
  statusCode: number;
  message: string;
  data: SimulationTargetMappingFilterData;
}

export interface SimulationTargetMappingRequest {
  actualTarget: string;
  simulatedTarget: string;
}

export interface UpdateSimulationTargetMappingRequest
  extends SimulationTargetMappingRequest {
  id: string;
}

export interface SimulationTargetMappingMutationResponse {
  isSuccess: boolean;
  statusCode: number;
  message: string;
  data?: SimulationTargetMapping;
}

@Injectable({
  providedIn: 'root'
})
export class SimulationTargetMappingService {

  private readonly baseUrl =
    `${GlobalConstants.API_URL}SimulationTargetMapping`;

  private mappingsCache: SimulationTargetMapping[] | null = null;
  private loadInFlight$: Observable<SimulationTargetMapping[]> | null = null;

  constructor(
    private readonly http: HttpClient
  ) {}

  filterWithDetails(
    request: SimulationTargetMappingFilterRequest
  ): Observable<SimulationTargetMappingFilterResponse> {
    return this.http.post<SimulationTargetMappingFilterResponse>(
      `${this.baseUrl}/FilterWithDetails`,
      request
    );
  }

  create(
    request: SimulationTargetMappingRequest
  ): Observable<SimulationTargetMappingMutationResponse> {
    return this.http.post<SimulationTargetMappingMutationResponse>(
      `${this.baseUrl}/Create`,
      request
    );
  }

  update(
    request: UpdateSimulationTargetMappingRequest
  ): Observable<SimulationTargetMappingMutationResponse> {

    const url =
      `${this.baseUrl}/Update?id=${encodeURIComponent(request.id)}`;

    return this.http.put<SimulationTargetMappingMutationResponse>(
      url,
      request
    );
  }

  ensureMappingsLoaded(): Observable<SimulationTargetMapping[]> {
    if (this.mappingsCache) {
      return of(this.mappingsCache);
    }

    if (this.loadInFlight$) {
      return this.loadInFlight$;
    }

    this.loadInFlight$ = this.filterWithDetails({
      pageNumber: 1,
      pageSize: 1000,
      searchValue: null,
      id: null,
      actualTarget: null,
      simulatedTarget: null
    }).pipe(
      map((response) =>
        response?.isSuccess ? (response.data?.list ?? []) : []
      ),
      tap((list) => {
        this.mappingsCache = list;
      }),
      catchError((error) => {
        console.warn('Unable to load simulation target mappings', error);
        this.mappingsCache = [];
        return of([]);
      }),
      finalize(() => {
        this.loadInFlight$ = null;
      }),
      shareReplay(1)
    );

    return this.loadInFlight$;
  }

  invalidateCache(): void {
    this.mappingsCache = null;
    this.loadInFlight$ = null;
  }

  /** Exact / base-name match: "Ammonia" ↔ "Ammonia (NH3)" */
  targetsMatch(left: string, right: string): boolean {
    const a = (left || '').trim().toLowerCase();
    const b = (right || '').trim().toLowerCase();
    if (!a || !b) {
      return false;
    }
    if (a === b) {
      return true;
    }
    const baseA = a.split('(')[0].trim();
    const baseB = b.split('(')[0].trim();
    return baseA === baseB;
  }

  /**
   * If alertType matches a selected actual target, return mapped simulatedTarget.
   */
  resolveSimulatedLabel(
    alertType: string,
    selectedTargets: string[]
  ): string | null {
    if (!alertType?.trim() || !selectedTargets?.length || !this.mappingsCache?.length) {
      return null;
    }

    const selected = selectedTargets.filter((t) => !!t?.trim());
    if (!selected.length) {
      return null;
    }

    const matchedSelected = selected.find((target) =>
      this.targetsMatch(target, alertType)
    );
    if (!matchedSelected) {
      return null;
    }

    const mapping =
      this.mappingsCache.find((m) =>
        this.targetsMatch(m.actualTarget, alertType)
      ) ??
      this.mappingsCache.find((m) =>
        this.targetsMatch(m.actualTarget, matchedSelected)
      ) ??
      null;

    const simulated = mapping?.simulatedTarget?.trim();
    return simulated || null;
  }
}
